import { Button } from '@almbrand/button';
import { Checkbox } from '@almbrand/checkbox';
import { LoadingSpinner } from '@almbrand/loadingspinner';
import { SelectField } from '@almbrand/selectfield';
import classNames from 'classnames';
import { ThemeNames } from 'constantsValues';
import { PageContext } from 'context/PageContext';
import { useSpdSearch } from 'hooks';
import { useContext, useEffect, useState } from 'react';
import { FieldValues, UseFormRegister, UseFormTrigger } from 'react-hook-form';
import styles from './CarSearch.module.scss';
import { LicensePlateInput } from './LicensePlateInput';
import { ResultBox } from './ResultBox';

type OptionsState = {
	brand: { errorMessage?: string; options: Option[] };
	year: { errorMessage?: string; options: Option[] };
	model: { errorMessage?: string; options: Option[] };
	variant: { errorMessage?: string; options: Option[] };
};

export type Option = {
	value: string;
	label: string;
};

export type ResultBoxProps = {
	header: string;
	subHeader: string;
};

export interface CarSearchProps {
	register: UseFormRegister<FieldValues>;
	trigger?: UseFormTrigger<FieldValues>;
	submitStep?: () => void;
	contentType: string;
	stepContentId: number;
	onComplete?: (licensePlate: string) => void;
	resultBox?: ResultBoxProps | null;
	licensePlateSearchField: SpdSearchType;
	makeSearchField: SpdSearchType;
	yearSearchField: SpdSearchType;
	modelSearchField: SpdSearchType;
	variantSearchField: SpdSearchType;
}

export const CarSearch: React.FC<CarSearchProps> = ({
	register,
	trigger,
	submitStep,
	contentType,
	stepContentId,
	onComplete,
	resultBox = null,
	licensePlateSearchField,
	makeSearchField,
	yearSearchField,
	modelSearchField,
	variantSearchField,
	...props
}) => {
	const pageContext = useContext(PageContext);
	const { theme } = pageContext ?? {};
	const themeName = ThemeNames[theme];
	const [makeAndModelState, setMakeAndModelState] = useState<
		{ name: string; param: { param_name: string; param_value: string } }[]
	>([]);
	const makeSearch = useSpdSearch({ spdSearchType: makeSearchField });
	const yearSearch = useSpdSearch({ spdSearchType: yearSearchField });
	const modelSearch = useSpdSearch({ spdSearchType: modelSearchField });
	const variantSearch = useSpdSearch({ spdSearchType: variantSearchField });
	const [showSpinner, setShowSpinner] = useState(false);
	const [showResult, setShowResult] = useState(false);
	const [noLicensePlate, setNoLicensePlate] = useState(false);
	const [options, setOptions] = useState<OptionsState>({
		brand: { options: [] },
		year: { options: [] },
		model: { options: [] },
		variant: { options: [] },
	});

	const { onChange, name } = register(stepContentId.toString(), { required: true });

	const handleLicensePlateChange = (formatted: string, isComplete: boolean) => {
		// Remove all spaces and make lower case before sending to parent
		const normalized = formatted.replace(/\s+/g, '').toLowerCase();
		if (isComplete) {
			setShowSpinner(true);
			setShowResult(false);
			onComplete?.(normalized);
		} else {
			setShowSpinner(false);
			setShowResult(false);
		}
	};

	// Show ResultBox and hide spinner when resultBox is set from parent
	useEffect(() => {
		if (resultBox) {
			setShowResult(true);
			setShowSpinner(false);
		}
	}, [resultBox]);

	useEffect(() => {
		// Only run if the last updated field was the brand (makeSearchField)
		const last = makeAndModelState[makeAndModelState.length - 1];
		if (last && last.name === makeSearchField.uniqueIdentifier.uid) {
			populateYearOptions();
		}

		if (last && last.name === yearSearchField.uniqueIdentifier.uid) {
			populateModelOptions();
		}

		if (last && last.name === modelSearchField.uniqueIdentifier.uid) {
			populateVariantOptions();
		}
	}, [makeAndModelState]);

	const updateMakeAndModelState = (
		setState: React.Dispatch<
			React.SetStateAction<{ name: string; param: { param_name: string; param_value: string } }[]>
		>,
		field: SpdSearchType,
		value: Option
	) => {
		setState((state) => {
			const existingIndex = state.findIndex((item) => item.name === field.uniqueIdentifier.uid);
			const paramName = field.searchConfiguration.outputFields.items[0].content.param;
			if (existingIndex !== -1) {
				// Update the existing item and remove all items after it
				const updated = state
					.slice(0, existingIndex + 1)
					.map((item, index) =>
						index === existingIndex ? { ...item, param: { ...item.param, param_value: value.value } } : item
					);
				return updated;
			}
			// If not found, just add the new item
			return [
				...state,
				{
					name: field.uniqueIdentifier.uid,
					param: {
						param_name: paramName,
						param_value: value.value,
					},
				},
			];
		});
	};

	const saveFieldChangeToForm = (
		register: UseFormRegister<FieldValues>,
		field: SpdSearchType,
		value: Option,
		contentType: string
	) => {
		const { onChange, name } = register(field.uniqueIdentifier.uid, { required: true });
		onChange({
			target: {
				name: name,
				value: {
					id: name,
					value: value.value,
					type: contentType,
				},
			},
		});
	};

	const handleCheckboxChange = async () => {
		setNoLicensePlate(!noLicensePlate);

		if (!noLicensePlate) {
			populateBrandOptions();
		}
	};

	const handleBrandChange = (value: Option): void => {
		updateMakeAndModelState(setMakeAndModelState, makeSearchField, value);
		saveFieldChangeToForm(register, makeSearchField, value, contentType);
	};

	const handleYearChange = (value: Option): void => {
		updateMakeAndModelState(setMakeAndModelState, yearSearchField, value);
		saveFieldChangeToForm(register, yearSearchField, value, contentType);
	};

	const handleModelChange = (value: Option): void => {
		updateMakeAndModelState(setMakeAndModelState, modelSearchField, value);
		saveFieldChangeToForm(register, modelSearchField, value, contentType);
	};

	const handleVariantChange = (value: Option): void => {
		updateMakeAndModelState(setMakeAndModelState, variantSearchField, value);

		onChange({
			target: {
				name: name,
				value: {
					id: name,
					label: value.label,
					value: value.value,
					type: contentType,
				},
			},
		});
	};

	// Utility to build params array for a given searchField
	const buildParams = (
		searchField: SpdSearchType,
		makeAndModelState: { name: string; param: { param_name: string; param_value: string } }[]
	) => {
		return searchField.searchConfiguration.inputFields.items.map((item) => {
			const found = makeAndModelState.find((state) => state.name === item.content.uniqueIdentifier.uid);
			return found ? found.param : { param_name: '', param_value: '' };
		});
	};

	// Utility to sanitize year labels (remove .0)
	const sanitizeYearResult = (result: Option[]) =>
		result.map((item) => ({
			...item,
			label: item.label.replace(/\.0$/, ''),
		}));

	// Utility to patch params for model and variant
	// TODO: Get this from input later on
	const patchParams = (
		params: { param_name: string; param_value: string }[],
		typePatch?: boolean,
		uniquePatch?: boolean
	) => {
		const patched = [...params];
		if (typePatch && patched.length > 1) {
			const index = patched.length - (uniquePatch ? 2 : 1);
			if (patched[index] && (patched[index].param_name === '' || patched[index].param_value === '')) {
				patched[index].param_name = 'TYPE';
				patched[index].param_value = '0001';
			}
		}
		if (uniquePatch && patched.length > 0) {
			const index = patched.length - 1;
			if (patched[index] && (patched[index].param_name === '' || patched[index].param_value === '')) {
				patched[index].param_name = 'UNIKT_ID';
				patched[index].param_value = '';
			}
		}
		return patched;
	};

	const [loading, setLoading] = useState({
		brand: false,
		year: false,
		model: false,
		variant: false,
	});

	const setLoadingKey = (key: keyof typeof loading, value: boolean) => {
		setLoading((prev) => ({ ...prev, [key]: value }));
	};

	const populateBrandOptions = async () => {
		setLoadingKey('brand', true);
		const result = await makeSearch.doMakeSearch();
		if (result) {
			setLoadingKey('brand', false);
			setOptions((prev) => ({ ...prev, brand: { options: result } }));
		}
	};

	const populateYearOptions = async () => {
		setLoadingKey('year', true);
		const params = buildParams(yearSearchField, makeAndModelState);
		const result = await yearSearch.doSearch({
			uuid: yearSearchField.searchConfiguration.spdUniqueIdentifier,
			params,
		});
		if (result) {
			setLoadingKey('year', false);
			setOptions((prev) => ({ ...prev, year: { options: sanitizeYearResult(result) } }));
		}
	};

	const populateModelOptions = async () => {
		setLoadingKey('model', true);
		let params = buildParams(modelSearchField, makeAndModelState);
		params = patchParams(params, true, false); // Patch last param for TYPE
		const result = await modelSearch.doSearch({
			uuid: modelSearchField.searchConfiguration.spdUniqueIdentifier,
			params,
		});
		if (result) {
			setLoadingKey('model', false);
			setOptions((prev) => ({ ...prev, model: { options: result } }));
		}
	};

	const populateVariantOptions = async () => {
		setLoadingKey('variant', true);
		let params = buildParams(variantSearchField, makeAndModelState);
		params = patchParams(params, true, true); // Patch last two params for TYPE and UNIKT_ID
		const result = await variantSearch.doSearch({
			uuid: variantSearchField.searchConfiguration.spdUniqueIdentifier,
			params,
		});
		if (result) {
			setLoadingKey('variant', false);
			setOptions((prev) => ({ ...prev, variant: { options: result } }));
		}
	};

	return (
		<div className={classNames(styles.CarSearch)}>
			{!noLicensePlate && <LicensePlateInput onChange={handleLicensePlateChange} />}

			<div className={classNames(styles.CarSearch__checkbox)}>
				<Checkbox
					name='checkbox'
					label='Jeg kender ikke min nummerplade'
					roundedCorners
					onChange={handleCheckboxChange}
				/>
			</div>
			{showSpinner && !noLicensePlate && (
				<div className={classNames(styles.CarSearch__spinner)}>
					<LoadingSpinner
						color='primary'
						style='small'
					/>
				</div>
			)}

			{showResult && resultBox && !noLicensePlate && <ResultBox {...resultBox} />}

			{noLicensePlate && (
				<>
					<SelectField
						themeName={themeName}
						options={options.brand.options}
						errorMessage={options.brand.errorMessage}
						icon={{ isIcon: false }}
						placeHolder='Bilmærke'
						isSearchable
						iconPosition='right'
						onChange={handleBrandChange}
						isLoading={loading.brand}
					/>
					<SelectField
						themeName={themeName}
						options={options.year.options}
						isDisabled={!options.brand.options.length}
						errorMessage={options.year.errorMessage}
						icon={{ isIcon: false }}
						placeHolder='Årgang'
						isSearchable
						iconPosition='right'
						onChange={handleYearChange}
						isLoading={loading.year}
					/>
					<SelectField
						themeName={themeName}
						options={options.model.options}
						isDisabled={!options.year.options.length}
						errorMessage={options.model.errorMessage}
						icon={{ isIcon: false }}
						placeHolder='Model'
						isSearchable
						iconPosition='right'
						onChange={handleModelChange}
						isLoading={loading.model}
					/>
					<SelectField
						themeName={themeName}
						options={options.variant.options}
						isDisabled={!options.model.options.length}
						errorMessage={options.variant.errorMessage}
						icon={{ isIcon: false }}
						placeHolder='Variant'
						isSearchable
						iconPosition='right'
						onChange={handleVariantChange}
						isLoading={loading.variant}
					/>
				</>
			)}
			{/* TODO: Remove this when SPD api is up and running again */}
			<br />
			<Button
				buttonType='personal'
				onClick={() =>
					onChange({
						target: {
							name: name,
							value: {
								id: name,
								label: 'UIID',
								value: '20231784',
								type: contentType,
							},
						},
					})
				}
			>
				{'Bypass Validation (UIID: 20231784)'}
			</Button>
		</div>
	);
};
